import { useState, useEffect, useRef, ReactNode } from "react";
import * as S from "./styles";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { FiSearch, FiX } from "react-icons/fi";
import { useTheme } from "styled-components";
import { Button } from "../Button";
import FilterIcon from "../../../assets/icons/filter-icon.svg";
import { useMediaQuery } from "usehooks-ts";
import { DrawerComponent } from "../Drawer";
import { useTranslation } from "react-i18next";

interface SearchInputProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  onPressEnter?: () => void;
  handleChangeSearch?: () => void;
  filterDropdown?: {
    selectInput: ReactNode;
    onFilterClick: () => void;
    onResetClick: () => void;
    disabled?: boolean;
  };
}

export function SearchInput({
  filterDropdown,
  handleChangeSearch,
  onPressEnter,
  ...rest
}: SearchInputProps) {
  const { newColors, responsiveDesign } = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${responsiveDesign.sm})`);
  const { t } = useTranslation();

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" && onPressEnter) {
      onPressEnter();
    }
  }

  const handleClear = () => {
    if (rest.onChange) {
      rest.onChange({
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    setIsClearing(true);
    inputRef.current?.focus();
  };

  useEffect(() => {
    if (isClearing && (rest.value === "" || rest.value === undefined)) {
      if (onPressEnter) {
        onPressEnter();
      }
      setIsClearing(false);
    }
  }, [rest.value, isClearing, onPressEnter]);

  return (
    <S.Container>
      <S.SearchButton
        disabled={filterDropdown?.disabled}
        onClick={onPressEnter || (() => {})}
        title={t("search")}
      >
        <FiSearch size={24} color={newColors["gray-900"]} />
      </S.SearchButton>

      <input
        ref={inputRef}
        type="text"
        placeholder={t("search")}
        onKeyDown={handleKeyDown}
        {...rest}
      />

      {rest.value && rest.onChange && (
        <S.IconButton onClick={handleClear} title={t("clearSearch")}>
          <FiX
            size={24}
            style={{ marginRight: "0.4rem" }}
            color={newColors["gray-900"]}
          />
        </S.IconButton>
      )}

      {handleChangeSearch && filterDropdown && (
        <S.IconButton
          disabled={filterDropdown.disabled}
          title={t("changeSearchField")}
        >
          <S.SwapIcon
            title={t("changeSearchField")}
            onClick={handleChangeSearch}
          />
        </S.IconButton>
      )}

      {filterDropdown && (
        <>
          {isMobile ? (
            <S.IconContainer>
              <DrawerComponent
                open={filterDrawerOpen}
                onOpenChange={setFilterDrawerOpen}
                variant="filter"
                description=""
                triggerContent={
                  <S.IconButton
                    disabled={filterDropdown.disabled}
                    title={t("filters")}
                    onClick={() => setFilterDrawerOpen(true)}
                  >
                    <img src={FilterIcon} alt="Filter icon" />
                  </S.IconButton>
                }
                width="100%"
                title={t("filterBy")}
              >
                <S.DrawerContent>
                  {filterDropdown.selectInput}
                  <S.ButtonContainer>
                    <Button
                      title={t("filter")}
                      style={{ width: "100%", height: "2.5rem" }}
                      onClick={() => {
                        filterDropdown.onFilterClick();
                        setFilterDrawerOpen(false);
                      }}
                    />
                    <Button
                      title={t("resetFilters")}
                      variant="ghost"
                      style={{ width: "100%", height: "2.5rem" }}
                      onClick={() => {
                        filterDropdown.onResetClick();
                        setFilterDrawerOpen(false);
                      }}
                    />
                  </S.ButtonContainer>
                </S.DrawerContent>
              </DrawerComponent>
            </S.IconContainer>
          ) : (
            <DropdownMenu.Root modal={false}>
              <DropdownMenu.Trigger asChild disabled={filterDropdown.disabled}>
                <S.IconButton title={t("filters")}>
                  <img src={FilterIcon} alt="Filter icon" />
                </S.IconButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <S.DropdownContent sideOffset={32}>
                  <S.Title>{t("filterBy")}</S.Title>
                  {filterDropdown.selectInput}
                  <S.ButtonContainer>
                    <Button
                      title={t("filter")}
                      style={{ width: "100%" }}
                      onClick={filterDropdown.onFilterClick}
                    />
                    <Button
                      title={t("resetFilters")}
                      variant="ghost"
                      style={{ width: "100%" }}
                      onClick={filterDropdown.onResetClick}
                    />
                  </S.ButtonContainer>
                </S.DropdownContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </>
      )}
    </S.Container>
  );
}
